import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton } from "@/components/Reveal";
import { LODGE } from "@/data/island";

export default function LodgePage() {
  return (
    <>
      <PageHero eyebrow="Collanmore Island Lodge" title={LODGE.title} image={LODGE.heroImage}>
        Complete privacy, exclusive use of the island and lodge facilities, and bespoke adventure
        and entertainment packages.
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Eyebrow>Exclusive Island Living</Eyebrow>
            <div className="mt-8 space-y-6">
              {LODGE.intro.map((p, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <p className="text-sm font-light leading-relaxed text-mist sm:text-base">{p}</p>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.15}>
              <ul className="mt-10 grid gap-3 sm:grid-cols-2">
                {LODGE.amenities.map((a) => (
                  <li
                    key={a}
                    className="flex items-start gap-3 border border-white/10 bg-navy-900/50 p-4 text-[13px] text-ivory/90"
                  >
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                    {a}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold/40" aria-hidden="true" />
              <img
                src={LODGE.mainImage}
                alt="Collanmore Island Lodge interior"
                data-testid="lodge-main-image"
                className="relative aspect-[4/5] w-full object-cover"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-navy-900 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeIn>
              <img
                src={LODGE.tomsBarImage}
                alt="Tom's Bar at Collanmore Island Lodge"
                data-testid="lodge-toms-bar-image"
                className="aspect-[4/3] w-full border border-white/10 object-cover"
              />
            </FadeIn>
            <div>
              <Eyebrow>Tom's Bar</Eyebrow>
              <FadeIn delay={0.1}>
                {LODGE.tomsBar.map((p, i) => (
                  <p key={i} className="mt-6 text-sm font-light leading-relaxed text-mist sm:text-base">
                    {p}
                  </p>
                ))}
              </FadeIn>
              <FadeIn delay={0.15}>
                <img
                  src={LODGE.tomsBarImage2}
                  alt="Guests enjoying Tom's Bar"
                  className="mt-8 aspect-[16/10] w-full border border-white/10 object-cover"
                />
              </FadeIn>
            </div>
          </div>

          <div className="mt-24 grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <Eyebrow>Modern Conveniences</Eyebrow>
              <FadeIn delay={0.1}>
                {LODGE.modern.map((p, i) => (
                  <p key={i} className="mt-6 text-sm font-light leading-relaxed text-mist sm:text-base">
                    {p}
                  </p>
                ))}
              </FadeIn>
            </div>
            <FadeIn className="order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={LODGE.hotTubImage}
                  alt="Private hot tub with views of Clew Bay"
                  data-testid="lodge-hottub-image"
                  className="aspect-square w-full border border-white/10 object-cover"
                />
                <img
                  src={LODGE.modernImage}
                  alt="Modern kitchen facilities at the Lodge"
                  className="aspect-square w-full border border-white/10 object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-800 pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={LODGE.nightImage}
                alt="Collanmore Island Lodge at night"
                data-testid="lodge-night-image"
                className="h-[440px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Evenings on the Island</p>
                <h3 className="mt-2 font-serif text-3xl text-ivory sm:text-4xl">The Lodge at night</h3>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-navy-950 py-24">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Eyebrow center>Check In</Eyebrow>
          <FadeIn delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl">
              If you are looking for a special and amazing place to stay, <em className="italic text-gold">you have found it</em>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm font-light leading-relaxed text-mist sm:text-base">
              We have three fantastic packages available at Collanmore Island Lodge.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <GoldButton href="/group-packages" testId="lodge-packages-cta">View Group Packages</GoldButton>
              <GoldButton href="/contact" testId="lodge-contact-cta" className="!bg-transparent border border-gold/70 !text-gold hover:!bg-gold-muted">
                Enquire Now
              </GoldButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
